// areas/settings/survey-questions/components/SurveyQuestionOutcomeModal.tsx
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'semantic-ui-react';

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (v: { title: string; scale: string[] }) => void | Promise<void>;
  // initial is optional and safely handled
  initial?: { title?: string; scale?: string[] };
  isEditing?: boolean;
};

const SurveyQuestionOutcomeModal: React.FC<Props> = ({ open, onClose, onSave, initial, isEditing = false }) => {
  const [title, setTitle] = useState('');
  const [low, setLow] = useState('Strongly Disagree');
  const [middle, setMiddle] = useState('');
  const [high, setHigh] = useState('Strongly Agree');

  useEffect(() => {
    if (open) {
      const t = initial?.title ?? '';
      const s = initial?.scale ?? [];
      setTitle(t);
      setLow(s[0] ?? 'Strongly Disagree');
      setMiddle(s.length === 3 ? s[1] ?? '' : '');
      setHigh(s[s.length - 1] ?? 'Strongly Agree');
    }
  }, [open, initial]);

  const canSave = title.trim() && low.trim() && high.trim();

  const handleSave = async () => {
    const scale = [low.trim(), ...(middle.trim() ? [middle.trim()] : []), high.trim()];
    await Promise.resolve(onSave({ title: title.trim(), scale }));
    onClose(); // close after successful save
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>{isEditing ? 'Edit Outcomes Question' : 'Add Outcomes Question'}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            label="Question"
            value={title}
            onChange={(_, { value }) => setTitle(String(value))}
          />
          <Form.Group widths="equal">
            <Form.Input
              label="Scale (low)"
              value={low}
              onChange={(_, { value }) => setLow(String(value))}
              placeholder="e.g., Rarely / Strongly Disagree"
            />
            <Form.Input
              label="Scale (middle — optional)"
              value={middle}
              onChange={(_, { value }) => setMiddle(String(value))}
              placeholder="e.g., Usually / Neutral"
            />
            <Form.Input
              label="Scale (high)"
              value={high}
              onChange={(_, { value }) => setHigh(String(value))}
              placeholder="e.g., Always / Strongly Agree"
            />
          </Form.Group>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={!canSave} onClick={handleSave}>Save</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default SurveyQuestionOutcomeModal;
